import { IViewModel } from 'mobx-utils';
import { ReactElement, ReactNode } from 'react';

type BufferModel<T> = T & IViewModel<T>;
type Fields<T> = keyof T;
type SortDirection = 'ascend' | 'descend';
type SimpleObject = Record<string, unknown>;

type ValidationStatus = '' | 'error' | 'warning';

type CSSPixelsNumber = `${number}px`;

enum FileStatus {
  UNTOUCHED,
  LOADING,
  ERROR_UPLOAD,
  LOADED,
}

enum MimeTypes {
  PDF = 'application/pdf',
  DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  JPEG = 'image/jpeg',
  CSV = 'text/csv',
  TIFF = 'image/tiff',
  BMP = 'image/bmp',
  XML = 'text/xml',
  JSON = 'application/json',
  AVI = 'video/x-msvideo',
  MOV = 'video/quicktime',
  PLAIN = 'text/plain',
  MKV = 'video/x-matroska',
  GIF = 'image/gif',
  PNG = 'image/png',
  MP4 = 'video/mp4',
  XLS = 'application/vnd.ms-excel',
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

enum UiNotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  WARN = 'warning',
  INFO = 'info',
}

enum LoadingStatus {
  LOADING,
  LOADED,
  LOADED_ERROR,
}

enum SelectionMode {
  PAGE = 'page',
  ALL = 'all',
}

enum HeaderCheckboxState {
  CHECKED = 'checked',
  INDETERMINATE = 'indeterminate',
  EMPTY = 'empty',
}

enum BootstrapStatus {
  INITIAL,
  LOADING,
  LOADED,
}

enum RestrictionsStatus {
  INITIAL,
  LOADING,
  LOADED,
}

enum ReportsAccessStatus {
  INITIAL,
  LOADING,
  LOADED,
}

enum MessageRole {
  ASSISTANT = 'assistant',
  USER = 'user',
  TRAINER_SYSTEM = 'trainer_system',
}

interface DataBookStatus {
  bootstrap: BootstrapStatus;
  restrictions: RestrictionsStatus;
  reportsAccess: ReportsAccessStatus;
}

interface HomeStatus {
  managerInfo: LoadingStatus;
  requestFeature: SubmittingStatus;
}

enum SubmittingStatus {
  UNTOUCHED,
  SUBMITTING,
  ERROR_SUBMITTING,
  REQUEST_HAS_BEEN_SENT,
}

enum OrderDirection {
  ASC,
  DESC,
}

interface UiNotification {
  title?: LocalizedMessageCode;
  text?: LocalizedMessageCode;
  type?: UiNotificationType;
  closable?: boolean;
}

interface IExtractedLinkInterface {
  href: string;
  text: string;
  fullTag: string;
}

type A = Fields<UiNotification>;

interface LocalNotification {
  title?: LocalizedMessageCode;
  text?: LocalizedMessageCode;
  type?: UiNotificationType;
  params?: {
    [x: string]: unknown;
  };
  closable?: boolean;
  closeTimeoutSec?: number;
  footerBtnConfig?: {
    type: 'primary' | 'default' | 'link' | 'text' | 'dashed';
    text: PlainText | LocalizedMessageCode;
    onClick: () => void;
  };
  contentLocalizeParams?: { [x: string]: unknown };
  onMessageLinkClick?: () => void;
}

type NotificationLinkClicked = () => void;

interface ExternalNotification {
  title?: PlainText | LocalizedMessageCode;
  text?: PlainText | ReactNode;
  type?: UiNotificationType;
  closable?: boolean;
  closeTimeoutSec?: number;
  onMessageLinkClick?: () => void;
  contentLocalizeParams?: { [x: string]: unknown };
  footerBtnConfig?: {
    type: 'primary' | 'default' | 'link' | 'text' | 'dashed';
    text: PlainText | LocalizedMessageCode;
    onClick: () => void;
  };
  onLinkClicked?: NotificationLinkClicked;
}

interface ListSort<SortKey> {
  field: SortKey;
  direction: SortDirection;
}

type Period = [Date | undefined, Date | undefined];
//TODO why here za*bal?
type DialabilityPhoneNumberFilter = [Dialability | undefined, Dialability | undefined];

interface ValueSimpleHistory<Value> {
  current: Value;
  applied: Value;
  set: (value: Value) => void;
  apply: () => void;
  reset: () => void;
  isDirty: () => boolean;
}

interface ValueListHistory<List, Value> {
  current: List;
  applied: List;
  set: (value: Value) => void;
  apply: () => void;
  reset: () => void;
  isDirty?: () => boolean;
}

interface Product {
  id?: ProductId;
  code: ProductCode;
  email: Email;
  name: PlainText;
  stracker_url: Url;
  manifest_path?: string;
  website_url?: Url;
}

type ProductCode = 'skorozvon' | 'beeline' | string;

type AccountingType = 'skorozvon' | 'yandex_cloud' | 'elama';

enum StatusCode {
  ACTIVE = 'active',
  PAUSED = 'paused',
  FAILED = 'failed',
  COMPLETED = 'completed',
}

type NotificationType = 'success' | 'error' | 'warning' | 'info';

type AllSelected = 'all';

interface MaskingHandlerResult<InputType> {
  updatedValue: InputType;
  updatedUnmaskedValue: InputType;
}

interface PhoneNumberMaskParams<InputType> {
  value: InputType;
  onlyRu?: boolean;
}

interface MaskByFormatParams<InputType> {
  value: InputType;
  format: string;
}

interface TelegramUsernameMaskParams<InputType> {
  value: InputType;
}

interface MessageDialogItem {
  role: MessageRole;
  message: PlainText | ReactNode;
  addonAfter?: ReactNode;
}

interface MaskingHandlers {
  phoneNumberMask: <InputType>(
    params: PhoneNumberMaskParams<InputType>,
  ) => MaskingHandlerResult<InputType>;
  maskByFormat: <InputType>(
    params: MaskByFormatParams<InputType>,
  ) => MaskingHandlerResult<InputType>;
  telegramUsernameMask: <InputType>(
    params: TelegramUsernameMaskParams<InputType>,
  ) => MaskingHandlerResult<InputType>;
}

enum SupportSourceType {
  NEW_FRONTEND = 'Робот в новом фронте',
  TRAINER = 'Тренер',
}

export {
  BootstrapStatus,
  RestrictionsStatus,
  ReportsAccessStatus,
  StatusCode,
  type DataBookStatus,
  LoadingStatus,
  MimeTypes,
  type ValidationStatus,
  type LocalNotification,
  type ExternalNotification,
  type BufferModel,
  UiNotificationType,
  type UiNotification,
  type IExtractedLinkInterface,
  type Fields,
  type SortDirection,
  type SimpleObject,
  type ListSort,
  type Product,
  type ProductCode,
  type NotificationType,
  type ValueSimpleHistory,
  type Period,
  type AllSelected,
  type CSSPixelsNumber,
  type ValueListHistory,
  type MaskingHandlers,
  type MaskingHandlerResult,
  type PhoneNumberMaskParams,
  type MaskByFormatParams,
  type TelegramUsernameMaskParams,
  type DialabilityPhoneNumberFilter,
  type NotificationLinkClicked,
  type MessageDialogItem,
  MessageRole,
  FileStatus,
  OrderDirection,
  SubmittingStatus,
  SupportSourceType,
  type HomeStatus,
  SelectionMode,
  HeaderCheckboxState,
  type AccountingType,
};
